/**
 * ProfilePage.tsx — the /patients/profile surface (PATIENT-gated).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: P1C (CTO UX batch 2026-07-16).
 *
 * WHY: one consolidated "Your details" card holds everything the patient can
 * see + change about themselves, edited behind a SINGLE "Edit profile" button:
 *
 *   • Name             — the patient's OWN first/last name (identity
 *                        `updateMyName`), editable.
 *   • Email            — READ-ONLY, always. It is the sign-in identifier; the
 *                        card tells the patient to contact us to change it. It
 *                        is never rendered as an input, even in edit mode.
 *   • Date of birth    — read-only display when identity carries it.
 *   • Delivery address — the current order's address (getDashboard →
 *                        currentPlan.orderCaseId), editable until approval; a
 *                        409 flips it to "locked — your clinician has approved".
 *                        No order yet → a quiet line explaining when it becomes
 *                        available. Saved via orders `updateDeliveryAddress`.
 *
 * The registered (home) address card was removed — it is not needed on the
 * patient profile. Name (identity) and delivery address (orders) save through
 * two services under the one button; both writes are idempotent, so a retry
 * after a partial failure is safe.
 *
 * The Password card stays separate below (a security action, not a "detail").
 *
 * EMPTY-ACCOUNT HANDLING mirrors the dashboard: a fresh account's dashboard
 * read may 404 (no row minted yet) or return the empty shape — either way the
 * profile still renders (only the delivery address has nothing to edit yet).
 * Every fetch failure is a retryable error state (TA-06).
 */
import { useCallback, useEffect, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { ApiError } from '../api/client';
import { changePassword, getPatientProfile, updateMyName } from '../api/identity';
import { getDashboard, updateDeliveryAddress } from '../api/orders';
import type { Address, CurrentPlan, DashboardData, PatientProfile } from '../api/types';
import { MIN_PASSWORD } from '../auth/forms';
import { formatAddress, formatUtcDate } from '../lib/format';
import { Alert, Button, Card, ErrorState, Field, Help, PageFrame, PasswordInput, TextInput, Title } from '../ui/kit';
import { IconLock, IconProfile } from '../ui/icons';
import { CardIconHeader, Row } from '../ui/sections';
import { SkeletonCard } from '../ui/Skeleton';
import { LeaveConfirmDialog } from '../navigation/LeaveConfirmDialog';
import { useUnsavedChangesGuard } from '../navigation/useUnsavedChangesGuard';

/** The empty dashboard shape a fresh account is treated as (matches Dashboard.tsx). */
const EMPTY_DASHBOARD: DashboardData = { currentPlan: null, orders: [], bills: [] };

export function ProfilePage() {
  const [profile, setProfile] = useState<PatientProfile | null>(null);
  const [plan, setPlan] = useState<CurrentPlan | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  const load = useCallback(() => {
    setState('loading');
    Promise.all([
      getPatientProfile(),
      getDashboard().catch((err: unknown) => {
        // A brand-new account may 404 (no dashboard row minted yet) — that is
        // simply "no order to show a delivery address for", not a failure.
        if (err instanceof ApiError && err.status === 404) return EMPTY_DASHBOARD;
        throw err;
      }),
    ])
      .then(([prof, dashboard]) => {
        setProfile(prof);
        setPlan(dashboard.currentPlan);
        setState('ready');
      })
      .catch(() => setState('error'));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (state === 'loading') {
    // Skeletons matching the stacked profile cards (details / password) keep the
    // layout stable while it loads (E3 #4).
    return (
      <PageFrame bare>
        <div style={{ display: 'grid', gap: 'var(--gps-s-4)' }}>
          <SkeletonCard lines={4} label="Loading your profile…" />
          <SkeletonCard lines={3} />
        </div>
      </PageFrame>
    );
  }

  if (state === 'error' || !profile) {
    return (
      <PageFrame bare>
        <ErrorState message="Sorry — we could not load your profile." onRetry={load} />
      </PageFrame>
    );
  }

  return (
    <PageFrame bare>
      <Title as="h2">Your profile</Title>

      <div style={{ display: 'grid', gap: 'var(--gps-s-4)', marginTop: 'var(--gps-s-3)' }}>
        <ProfileDetailsCard profile={profile} plan={plan} onProfileSaved={setProfile} />
        <PasswordCard />
      </div>
    </PageFrame>
  );
}

/** A small bold sub-heading for a section inside the consolidated details card. */
function SubLabel({ children }: { children: ReactNode }) {
  return (
    <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gps-muted)', margin: 'var(--gps-s-2) 0 var(--gps-s-2)' }}>
      {children}
    </p>
  );
}

/** Dimmed, non-interactive styling for the fields shown but not editable here. */
const DIM_FIELD: CSSProperties = { opacity: 0.55, cursor: 'not-allowed' };

/**
 * Your details — ONE card holding the editable name + delivery address
 * alongside read-only account facts (email, DOB). A single "Edit profile"
 * button opens every editable field at once. Email is NEVER an input (it is the
 * sign-in identifier — contact us to change). Delivery address is order-bound:
 * editable only while an order exists and the clinician has not yet approved (a
 * 409 flips it to locked); with no order it shows a note. Name saves via
 * identity, delivery via orders — both idempotent, so a retry after a partial
 * failure is safe. TA-06: a failed save keeps the form open with an error and
 * never claims success.
 */
function ProfileDetailsCard({
  profile,
  plan,
  onProfileSaved,
}: {
  profile: PatientProfile;
  plan: CurrentPlan | null;
  onProfileSaved: (p: PatientProfile) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [firstName, setFirstName] = useState(profile.firstName ?? '');
  const [lastName, setLastName] = useState(profile.lastName ?? '');

  const orderCaseId = plan?.orderCaseId;
  const [savedAddress, setSavedAddress] = useState<Address | null>(plan?.deliveryAddress ?? null);
  const [locked, setLocked] = useState(plan?.state === 'active');
  const [line1, setLine1] = useState(savedAddress?.line1 ?? '');
  const [line2, setLine2] = useState(savedAddress?.line2 ?? '');
  const [city, setCity] = useState(savedAddress?.city ?? '');
  const [postcode, setPostcode] = useState(savedAddress?.postcode ?? '');

  const hasName = Boolean(profile.firstName && profile.lastName);
  // The delivery address can only be edited while there is an order that the
  // clinician has not yet approved.
  const deliveryEditable = Boolean(orderCaseId) && !locked;

  function startEditing() {
    setFirstName(profile.firstName ?? '');
    setLastName(profile.lastName ?? '');
    setLine1(savedAddress?.line1 ?? '');
    setLine2(savedAddress?.line2 ?? '');
    setCity(savedAddress?.city ?? '');
    setPostcode(savedAddress?.postcode ?? '');
    setError(null);
    setEditing(true);
  }

  async function handleSave() {
    if (busy) return;
    // Only the name the patient actually changed is written. A pre-capture patient
    // (no name captured yet) editing just their delivery address is NOT forced to add
    // a name first; but an existing name can never be blanked or left half-filled.
    const nameChanged =
      firstName.trim() !== (profile.firstName ?? '') || lastName.trim() !== (profile.lastName ?? '');
    if (nameChanged && (!firstName.trim() || !lastName.trim())) {
      setError('Please add your first and last name.');
      return;
    }
    // Build the delivery address only when it is editable AND actually changed —
    // editing just your name must not re-PATCH (and risk a surprise 409 lock) an
    // unchanged address.
    let nextAddress: Address | null = null;
    if (deliveryEditable) {
      const norm = { line1: line1.trim(), line2: line2.trim(), city: city.trim(), postcode: postcode.trim().toUpperCase() };
      const changed =
        norm.line1 !== (savedAddress?.line1 ?? '') ||
        norm.line2 !== (savedAddress?.line2 ?? '') ||
        norm.city !== (savedAddress?.city ?? '') ||
        norm.postcode !== (savedAddress?.postcode ?? '').toUpperCase();
      if (changed) {
        if (!norm.line1 || !norm.city || norm.postcode.length < 5) {
          setError('Please add the first line, town/city and a valid postcode for your delivery address.');
          return;
        }
        nextAddress = { line1: norm.line1, ...(norm.line2 ? { line2: norm.line2 } : {}), city: norm.city, postcode: norm.postcode };
      }
    }

    setBusy(true);
    setError(null);
    try {
      // Write ONLY what changed. A no-op name re-write would add a spurious
      // "name updated" row to the identity AuditEntry table — the source of truth
      // for clinician-attributed analytics — so an address-only edit never touches it.
      if (nameChanged) {
        const updated = await updateMyName(firstName.trim(), lastName.trim());
        onProfileSaved(updated);
      }

      if (nextAddress && orderCaseId) {
        try {
          await updateDeliveryAddress(orderCaseId, nextAddress);
          setSavedAddress(nextAddress);
        } catch (err) {
          if (err instanceof ApiError && err.status === 409) {
            // The clinician approved mid-edit — the address is now locked. Any name
            // change already saved, so close the editor and surface the lock.
            setLocked(true);
            setEditing(false);
            return;
          }
          // The address did not save. Keep the editor open to retry (re-saving the
          // same name is harmless — idempotent, and skipped when the name is unchanged).
          setError(
            nameChanged
              ? 'Your name was saved, but we could not update the delivery address. Please try again.'
              : 'We could not update the delivery address. Please try again.',
          );
          return;
        }
      }
      setEditing(false);
    } catch {
      setError('Sorry — we could not save your changes. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardIconHeader icon={<IconProfile />} title="Your details" />

      {editing ? (
        <>
          {error ? (
            <Alert tone="danger" role="alert" style={{ marginBottom: 'var(--gps-s-3)' }}>
              {error}
            </Alert>
          ) : null}

          <div style={{ display: 'grid', gap: 'var(--gps-s-3)', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            <Field label="First name" htmlFor="profile-first-name">
              <TextInput id="profile-first-name" value={firstName} autoComplete="given-name" onChange={(e) => setFirstName(e.target.value)} />
            </Field>
            <Field label="Last name" htmlFor="profile-last-name">
              <TextInput id="profile-last-name" value={lastName} autoComplete="family-name" onChange={(e) => setLastName(e.target.value)} />
            </Field>
          </div>

          {/* Email + DOB share one row, shown as fields for a consistent form but
              DISABLED and dimmed — they cannot be changed here (email is the sign-in). */}
          <div style={{ display: 'grid', gap: 'var(--gps-s-3)', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            <Field label="Email" htmlFor="profile-email">
              <TextInput id="profile-email" value={profile.email} disabled readOnly style={DIM_FIELD} />
            </Field>
            {profile.dateOfBirth ? (
              <Field label="Date of birth" htmlFor="profile-dob">
                <TextInput id="profile-dob" value={formatUtcDate(profile.dateOfBirth)} disabled readOnly style={DIM_FIELD} />
              </Field>
            ) : null}
          </div>

          <SubLabel>Delivery address</SubLabel>
          {deliveryEditable ? (
            <>
              <div className="gps-address-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gps-s-3)' }}>
                <Field label="Address line 1" htmlFor="delivery-line1">
                  <TextInput id="delivery-line1" value={line1} autoComplete="address-line1" onChange={(e) => setLine1(e.target.value)} />
                </Field>
                <Field label="Address line 2 (optional)" htmlFor="delivery-line2">
                  <TextInput id="delivery-line2" value={line2} autoComplete="address-line2" onChange={(e) => setLine2(e.target.value)} />
                </Field>
              </div>
              <div className="gps-address-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gps-s-3)' }}>
                <Field label="Town or city" htmlFor="delivery-city">
                  <TextInput id="delivery-city" value={city} autoComplete="address-level2" onChange={(e) => setCity(e.target.value)} />
                </Field>
                <Field label="Postcode" htmlFor="delivery-postcode">
                  <TextInput id="delivery-postcode" value={postcode} autoComplete="postal-code" onChange={(e) => setPostcode(e.target.value)} />
                </Field>
              </div>
            </>
          ) : orderCaseId && savedAddress ? (
            // Order approved — the address exists but is locked. Show it as a
            // dimmed, disabled field alongside the rest.
            <Field label="Address" htmlFor="delivery-locked" hint="Locked — your clinician has approved your treatment.">
              <TextInput id="delivery-locked" value={formatAddress(savedAddress)} disabled readOnly style={DIM_FIELD} />
            </Field>
          ) : (
            <Help style={{ marginBottom: 0 }}>
              You’ll set a delivery address when you place your first order — it appears here once you have one.
            </Help>
          )}

          <div style={{ display: 'flex', gap: 'var(--gps-s-2)', marginTop: 'var(--gps-s-4)', flexWrap: 'wrap' }}>
            <Button type="button" variant="primary" disabled={busy} onClick={() => void handleSave()}>
              {busy ? 'Saving…' : 'Save profile'}
            </Button>
            <Button type="button" variant="ghost" disabled={busy} onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </>
      ) : (
        <>
          {hasName ? (
            <Row label="Name" value={`${profile.firstName} ${profile.lastName}`} />
          ) : (
            <Help>Add your name so we can personalise your care.</Help>
          )}
          <Row label="Email" value={profile.email} />
          {profile.dateOfBirth ? <Row label="Date of birth" value={formatUtcDate(profile.dateOfBirth)} /> : null}
          {savedAddress ? (
            <Row label="Delivery address" value={formatAddress(savedAddress)} />
          ) : (
            <Help style={{ margin: 'var(--gps-s-2) 0 0' }}>
              You’ll set a delivery address when you place your first order — it appears here once you have one.
            </Help>
          )}
          {locked && savedAddress ? (
            <Alert tone="info" style={{ marginTop: 'var(--gps-s-2)' }}>
              Locked — your clinician has approved your treatment, so the delivery address can no longer be changed.
            </Alert>
          ) : null}

          <Button type="button" variant="ghost" onClick={startEditing} style={{ marginTop: 'var(--gps-s-3)' }}>
            Edit profile
          </Button>
        </>
      )}
    </Card>
  );
}

/**
 * Change password — current + new (min 10, matching identity's Zod rule) with
 * a client-side confirm field. A 403 is "current password is incorrect";
 * success confirms and notes that other devices are signed out (identity
 * revokes every other session, keeping this one).
 *
 * DIRTY (navigation resilience): dirty while ANY field is non-empty and the
 * change has not yet succeeded — guards a real router navigation away
 * (browser Back/tab close/refresh); no in-app Back/Cancel button lives on
 * this card, so there is nothing else to route through `guardedAction`.
 */
function PasswordCard() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const dirty = !done && Boolean(current || next || confirm);
  const guard = useUnsavedChangesGuard(dirty);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setError(null);
    setDone(false);
    if (!current) {
      setError('Enter your current password.');
      return;
    }
    if (next.length < MIN_PASSWORD) {
      setError(`Use at least ${MIN_PASSWORD} characters for your new password.`);
      return;
    }
    if (next !== confirm) {
      setError('The new passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await changePassword(current, next);
      setDone(true);
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setError('Current password is incorrect.');
      else setError('Sorry — we could not change your password. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <LeaveConfirmDialog open={guard.promptOpen} onStay={guard.cancelLeave} onLeave={guard.confirmLeave} />
      <CardIconHeader icon={<IconLock />} title="Password" />
      {done ? (
        <Alert tone="success" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
          Your password has been changed. For security, any other devices have been signed out.
        </Alert>
      ) : null}
      <form onSubmit={handleSubmit} aria-label="Change password" noValidate>
        <Field label="Current password" htmlFor="pw-current">
          <PasswordInput id="pw-current" value={current} autoComplete="current-password" onChange={(e) => setCurrent(e.target.value)} />
        </Field>
        <Field label="New password" htmlFor="pw-new" hint={`At least ${MIN_PASSWORD} characters.`}>
          <PasswordInput id="pw-new" value={next} autoComplete="new-password" onChange={(e) => setNext(e.target.value)} />
        </Field>
        <Field label="Confirm new password" htmlFor="pw-confirm">
          <PasswordInput id="pw-confirm" value={confirm} autoComplete="new-password" onChange={(e) => setConfirm(e.target.value)} />
        </Field>
        {error ? (
          <Alert tone="danger" role="alert" style={{ marginBottom: 'var(--gps-s-3)' }}>
            {error}
          </Alert>
        ) : null}
        <Button type="submit" variant="primary" disabled={busy}>
          {busy ? 'Changing…' : 'Change password'}
        </Button>
      </form>
    </Card>
  );
}
